import { useState } from 'react'
import { DollarSign, User, Building2, Globe, MessageSquare } from 'lucide-react'
import { tradingService } from '@/services/tradingService'
import { formatCurrency, formatDate, parseNumberInput } from '@/utils'
import { OrderDetails } from '@/types'
import { Modal } from './Modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'

export interface PlaceBidModalProps {
  isOpen: boolean
  onClose: () => void
  order: OrderDetails
  onBidPlaced: () => void
}

export const PlaceBidModal = ({ isOpen, onClose, order, onBidPlaced }: PlaceBidModalProps) => {
  const [formData, setFormData] = useState({
    tranche_size: '',
    valuation: '',
    email: '',
    name: '',
    company: '',
    job_title: '',
    website: '',
    telegram_username: '',
    linkedin: '',
    x_username: ''
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // Parse the input values (handles formats like 100k, 1m, 1B, 100,000, etc.)
      const parsedTrancheSize = parseNumberInput(formData.tranche_size);
      const parsedValuation = parseNumberInput(formData.valuation);

      if (isNaN(parsedTrancheSize) || parsedTrancheSize <= 0) {
        setError('Please enter a valid amount for tranche size.');
        return;
      }

      if (isNaN(parsedValuation) || parsedValuation <= 0) {
        setError('Please enter a valid valuation.');
        return;
      }

      await tradingService.placeBid({
        order_id: order.order_id,
        tranche_size: parsedTrancheSize,
        valuation: parsedValuation,
        type: order.type,
        email: formData.email,
        name: formData.name,
        company: formData.company || undefined,
        job_title: formData.job_title || undefined,
        website: formData.website || undefined,
        telegram_username: formData.telegram_username || undefined,
        linkedin: formData.linkedin || undefined,
        x_username: formData.x_username || undefined
      })
      
      onBidPlaced()
      onClose()
    } catch (err) {
      setError('Failed to place bid. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }))
  }

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title="Place Bid"
      contentClassName="max-w-2xl bg-background border border-border"
    >
      {/* Order Summary */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <h3 className="font-semibold text-lg mb-3">{order.project_name}</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Deal Type:</span>
              <Badge variant="outline">{order.deal_type}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Available:</span>
              <span className="font-semibold">{formatCurrency(order.offered_amount)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">FDV:</span>
              <span className="font-semibold">{formatCurrency(order.offered_fully_diluted_value)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Date:</span>
              <span className="font-semibold">{formatDate(order.date)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Financial Details */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Financial Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Tranche Size <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  type="text"
                  name="tranche_size"
                  value={formData.tranche_size}
                  onChange={handleChange}
                  placeholder="e.g., 100000, 100k, 1m, 1B"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Valuation <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  type="text"
                  name="valuation"
                  value={formData.valuation}
                  onChange={handleChange}
                  placeholder="e.g., 100000, 100k, 1m, 1B"
                  className="pl-10"
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Personal Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Personal Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Full Name <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className="pl-10"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                Email <span className="text-destructive">*</span>
              </label>
              <div className="relative">
                <MessageSquare className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  className="pl-10"
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Company</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  placeholder="Enter company name"
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Job Title</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  type="text"
                  name="job_title"
                  value={formData.job_title}
                  onChange={handleChange}
                  placeholder="Enter job title"
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </div>

        <Separator />

        {/* Contact Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground">Contact Information</h3>
          
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">Website</label>
            <div className="relative">
              <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <Input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://yourwebsite.com"
                className="pl-10"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Telegram</label>
              <Input
                type="text"
                name="telegram_username"
                value={formData.telegram_username}
                onChange={handleChange}
                placeholder="@username"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">LinkedIn</label>
              <Input
                type="text"
                name="linkedin"
                value={formData.linkedin}
                onChange={handleChange}
                placeholder="Profile URL"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">X (Twitter)</label>
              <Input
                type="text"
                name="x_username"
                value={formData.x_username}
                onChange={handleChange}
                placeholder="@username"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
          >
            {loading ? 'Placing Bid...' : 'Place Bid'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default PlaceBidModal 